import React, { useCallback, useEffect, useMemo, useState } from 'react'
import CarSeriesHome from 'Pages/auto/series/home'
import { IHeader } from 'Pages/auto/series/header'
import { IPk } from 'Pages/auto/series/pk'
import Breadcrumb from 'Components/breadcrumb'
import TopAnchor from 'Pages/auto/series/top-anchor'
import Head from 'next/head'
import { useRouter } from 'next/router'
import { stringify } from 'querystring'
import { IItemInfo } from 'Components/series/leaderboard'
import QA from 'Pages/auto/series/qa/home'
import { GetServerSideProps } from 'next'
import getUrl from 'lib/url'
import fetchFeed from 'lib/fetch-feed'
import Article from 'Pages/auto/series/article/home'
import Video from 'Pages/auto/series/video/home'
import fetch from 'lib/decorated-fetch'
import { isNewVersion } from 'lib/version'
import getQuery from '@arsenal/arsenal/modules/getQuery'
interface IProps {
  params: IHeader
  seriesId: number
  seriesName: string
  compare: IPk[]
  videoManualData: any
  photosData: any[]
  rankData: IItemInfo[]
  hasVR?: boolean
}

const getType = (path: string) => path?.match(/auto\/series\/\d+\/(\w+)/)?.[1]

function Series({
  params,
  seriesId,
  seriesName,
  compare,
  videoManualData,
  photosData,
  rankData,
  hasVR,
}: IProps) {
  const titles = [
    {
      name: '车系首页',
      url: `/auto/series/${seriesId}`,
    },
    {
      name: '参数配置',
      url: `/auto/params?carIds=${params?.car_id_list}`,
      outer: true,
    },
    {
      name: '车友圈',
      url: `/community/${seriesId}`,
      outer: true,
    },
    {
      name: '问答',
      url: `/auto/series/${seriesId}/qa`,
    },
    {
      name: '实拍大图',
      url: `/auto/series/${seriesId}/images`,
      outer: true,
    },
    {
      name: '文章',
      url: `/auto/series/${seriesId}/article`,
    },
    {
      name: '视频',
      url: `/auto/series/${seriesId}/video`,
    },
  ]

  const router = useRouter()
  const type = getType(router.asPath)
  // 切换背景色
  useEffect(() => {
    const color = document.body.style.backgroundColor
    document.body.style.backgroundColor = '#f7f8fc'
    return () => {
      document.body.style.backgroundColor = color
    }
  }, [])
  const activeIndex = useMemo(() => {
    return type ? type : 'home'
  }, [type])

  const renderPage = () => {
    switch (type) {
    case undefined:
      return (
        <CarSeriesHome
          params={params}
          seriesId={seriesId}
          seriesName={seriesName}
          compare={compare}
          videoManualData={videoManualData}
          photosData={photosData}
          hasVR={hasVR}
        />
      )
    case 'qa':
      return (
        <QA rankData={rankData} seriesId={seriesId} seriesName={seriesName} />
      )
    case 'article':
      return <Article rankData={rankData} seriesId={seriesId} />
    case 'video':
      return <Video rankData={rankData} seriesId={seriesId} />
    }
  }

  useEffect(() => {
    // 支持url参数定位具体板块
    const id = getQuery('anchorId')
    if (id) {
      const element = document?.querySelector(`#${id}`) as HTMLElement
      if (element?.offsetTop) {
        window.scrollTo(0, element?.offsetTop - 80)
      }
    }
  }, [getQuery('anchorId')])
  return (
    <>
      <Head>
        <title key="title">
          【{seriesName}】{seriesName}_{params.brand_name}品牌_
          {seriesName}报价_{seriesName}图片_懂车帝
        </title>
        <meta
          key="keywords"
          name="keywords"
          content={`${seriesName},${seriesName}报价多少,${seriesName}图片及价格,${seriesName}最低多少钱`}
        />
        <meta
          key="description"
          name="description"
          content={`懂车帝${seriesName}频道,提供${seriesName}报价,${seriesName}在售${seriesName}图片及价格,${seriesName}全部参数配置,${seriesName}最低多少钱文章,${seriesName}保养周期及费用等最新信息,${seriesName}最精彩汽车内容尽在懂车帝。`}
        />
      </Head>
      <div className="wrapper">
        <Breadcrumb
          path={[
            { name: '首页', href: '/home', as: '/' },
            {
              name: params.brand_name,
              href: `/auto/library/x-x-x-x-x-x-x-x-x-x-x?key=brand&param=${params.brand_id}&text=${params.brand_name}`,
              as: `/auto/library/x-x-x-x-x-x-x-x-x-x-x?key=brand&param=${params.brand_id}&text=${params.brand_name}`,
            },
            { name: params.name },
          ]}
          hideLine
        />

        <TopAnchor
          params={{
            seriesId,
            seriesName,
            dealerPrice: params.dealer_price,
            titles,
            activeIndex,
          }}
        />

        {renderPage()}
      </div>
      <style jsx>
        {`
          .wrapper {
            width: 1196px;
            margin: 10px auto 0;
          }
        `}
      </style>
    </>
  )
}

Series.getInitialProps = async (context: any) => {
  const {
    asPath,
    query: { seriesId },
    req,
  } = context

  const type = getType(asPath)
  let rankData = []
  if (type !== undefined) {
    try {
      const json = await fetch(
        getUrl(
          `/motor/m_api/pc_series_content_rank?${stringify({
            series_id: seriesId,
            type: type === 'qa' ? 'wenda' : type,
          })}`,
          'motor.m_station.api'
        ),
        { req }
      )
      rankData = json.data?.list || []
    } catch (err) {
      rankData = []
    }
  }

  const getHead = await fetch(
    getUrl(
      `/motor/car_page/m/v1/get_head/?series_id=${seriesId}`,
      'motor.car_page.api'
    ),
    { req }
  ).catch((err) => {
    // no-op
  })

  let fetchFeedRes
  try {
    fetchFeedRes = await fetchFeed(
      {
        tt_from: 'enter_auto',
        concern_id: parseInt(seriesId as string, 10),
        category: 'video',
        sub_category: 'video',
      },
      req
    )
  } catch (err) {
    // no-op
  }

  const videoList = await fetch(
    getUrl(
      `/motor/car_page/m/v6/video/guide_video_list?series_id=${seriesId}`,
      'motor.car_page.go_api'
    ),
    { req }
  )
    .then((res) => {
      return res?.data || []
    })
    .catch((err) => {
      // no-op
    })
  const getPictureHead = await fetch(
    getUrl(
      `/motor/car_page/m/v3/get_picture_head/?offset=0&series_id=${seriesId}&zt=default`,
      'motor.car_page.api'
    ),
    { req }
  )
    .then((res) => {
      return res?.data?.all_list || []
    })
    .catch((err) => {
      //  no-op
    })

  const hasVR = await fetch(
    getUrl(`/motor/pc/car/series/quan_jing?series_id=${seriesId}`)
  )
    .then((res) => res.data?.has_vr)
    .catch((err) => {
      // no-op
    })

  return {
    seriesId,
    params: getHead?.concern_obj || {},
    seriesName: getHead?.concern_obj?.name || '',
    compare: getHead?.recommended_compare_series_list || [],
    featuredData: fetchFeedRes || {},
    videoManualData: videoList || {
      data_list: [],
    },
    photosData: getPictureHead || [],
    hasVR,
    rankData,
    _pageType: isNewVersion(req?._isNewVersion) ? 'full auto-nav' : '',
  }
}

export default Series
